import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Paper, Card, CardContent, Chip, Divider, List, ListItem, ListItemText
} from '@mui/material';
import { format } from 'date-fns';
import { projectService, ProjectDetails } from '../services/projectService';
import { ProjectStatus } from '../types/project';

const statusColors: Record<ProjectStatus, 'default' | 'info' | 'success' | 'error'> = {
  PENDING: 'default',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const ProjectDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      const fetchProjectDetails = async () => {
        try {
          setLoading(true);
          const data = await projectService.getProjectDetails(Number(id));
          setProject(data);
        } catch (err) {
          setError('Failed to fetch project details.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchProjectDetails();
    }
  }, [id]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!project) {
    return <Alert severity="info">No project data found.</Alert>;
  }

  const totalRevenue = project.payments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
  const totalExpenses = project.expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);
  const netProfit = totalRevenue - totalExpenses;

  const totalPayable = project.expenses
    .filter(e => e.status === 'PAYABLE')
    .reduce((sum, e) => sum + parseFloat(e.amount), 0);
  
  const projectValue = project.project_value ? parseFloat(String(project.project_value)) : 0;
  const totalReceivable = projectValue - totalRevenue;

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>{project.name}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Client: {project.client_details?.name}
            </Typography>
            {project.project_value && (
                <Typography variant="h6" color="text.primary" sx={{ mt: 1 }}>
                    Project Value: ${Number(project.project_value).toFixed(2)}
                </Typography>
            )}
          </Box>
          <Chip label={project.status.replace('_', ' ')} color={statusColors[project.status]} sx={{ textTransform: 'capitalize' }} />
        </Box>
        <Typography variant="body1">{project.description}</Typography>
      </Paper>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1.5 }}>
        <Box sx={{ p: 1.5, width: { xs: '100%', md: 1/3 } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Financials</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1, pr: 2 }}>
                        <Typography>Total Payable: ${totalPayable.toFixed(2)}</Typography>
                        <Typography>Total Receivable: ${totalReceivable.toFixed(2)}</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6">&nbsp;</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ flex: 1, pl: 2 }}>
                        <Typography>Total Revenue: ${totalRevenue.toFixed(2)}</Typography>
                        <Typography>Total Expenses: ${totalExpenses.toFixed(2)}</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" color={netProfit >= 0 ? 'success.main' : 'error.main'}>
                            Net Profit: ${netProfit.toFixed(2)}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ p: 1.5, width: { xs: '100%', md: 2/3 } }}>
            <Card>
                <CardContent>
                <Typography variant="h6" gutterBottom>Payments Received ({project.payments.length})</Typography>
                <List dense>
                    {project.payments.map(payment => (
                    <ListItem key={payment.id} secondaryAction={`$${parseFloat(payment.amount).toFixed(2)}`}>
                        <ListItemText 
                        primary={payment.description} 
                        secondary={payment.date_received && !isNaN(new Date(payment.date_received).getTime()) ? format(new Date(payment.date_received), 'MMM dd, yyyy') : 'N/A'} 
                        />
                    </ListItem>
                    ))}
                </List>
                </CardContent>
            </Card>
        </Box>

        <Box sx={{ p: 1.5, width: { xs: '100%', md: 1/2 } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Expenses ({project.expenses.length})</Typography>
              <List dense>
                {project.expenses.map(expense => (
                  <ListItem key={expense.id} secondaryAction={`$${parseFloat(expense.amount).toFixed(2)}`}>
                    <ListItemText primary={expense.description || expense.expense_type} secondary={expense.date && !isNaN(new Date(expense.date).getTime()) ? format(new Date(expense.date), 'MMM dd, yyyy') : 'N/A'} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ p: 1.5, width: { xs: '100%', md: 1/2 } }}>
            <Card>
                <CardContent>
                <Typography variant="h6" gutterBottom>Tasks ({project.tasks.length})</Typography>
                <List dense>
                    {project.tasks.map(task => (
                    <ListItem key={task.id} secondaryAction={<Chip size="small" label={task.status.replace('_', ' ')} />}>
                        <ListItemText primary={task.title} secondary={`Assigned to: ${task.assigned_to_details?.first_name ?? 'N/A'}`} />
                    </ListItem>
                    ))}
                </List>
                </CardContent>
            </Card>
        </Box>

      </Box>
    </Box>
  );
};

export default ProjectDetailView;
